'use client';

import {useState, useMemo, useEffect, useCallback} from 'react';
import Image, {StaticImageData} from 'next/image';
import {cn} from '@purpose/ui/lib/utils';
import Fallback from '../../assets/static/Fallback.svg';

// Global cache for loaded images to prevent flickering on remount
const loadedImagesCache = new Set<string>();

const isValidSrc = (
  src: string | StaticImageData | null | undefined
): boolean => {
  if (!src) return false;
  if (typeof src !== 'string') return true;
  try {
    new URL(src);
    return true;
  } catch {
    return false;
  }
};

interface ImageWithFallbackProps {
  src?: string | StaticImageData | null;
  alt?: string;
  height?: string | number;
  width?: string | number;
  maxHeight?: string | number;
  maxWidth?: string | number;
  fill?: boolean;
  className?: string;
  iconStyles: {width: number; height: number};
  fallbackWrapperClassName?: string;
  onClick?: () => void;
  priority?: boolean;
  loading?: 'lazy' | 'eager';
  placeholder?: 'blur' | 'empty';
  blurDataURL?: string;
  sizes?: string;
  quality?: number;
  style?: React.CSSProperties;
  containerStyle?: React.CSSProperties;
  unoptimized?: boolean;
  aspectRatio?: number;
}

const ImageWithFallback = ({
  src,
  alt,
  height,
  width,
  maxHeight,
  maxWidth,
  fill = false,
  className,
  fallbackWrapperClassName,
  iconStyles,
  onClick,
  priority = false,
  unoptimized = false,
  loading = 'lazy',
  placeholder = 'empty',
  blurDataURL,
  sizes,
  quality = 75,
  style,
  containerStyle,
  aspectRatio
}: ImageWithFallbackProps) => {
  const [hasError, setHasError] = useState(false);

  const srcKey = typeof src === 'string' ? src : src?.toString() || '';
  const isImageCached = loadedImagesCache.has(srcKey);

  const [isLoading, setIsLoading] = useState(!isImageCached);

  useEffect(() => {
    if (srcKey && loadedImagesCache.has(srcKey)) {
      setIsLoading(false);
    }
  }, [srcKey]);

  const handleLoad = useCallback(() => {
    setIsLoading(false);
    if (srcKey) loadedImagesCache.add(srcKey);
  }, [srcKey]);

  const handleError = useCallback(() => {
    setHasError(true);
    setIsLoading(false);
  }, []);

  // Memoize the fallback component to prevent unnecessary re-renders
  const fallbackComponent = useMemo(
    () => (
      <div
        onClick={onClick}
        className={cn(
          'flex h-full items-center justify-center bg-linear-to-b from-[#F9F9F9] to-[#E8E5E5]',
          fallbackWrapperClassName
        )}
        style={{
          width: fill ? '100%' : width,
          aspectRatio: aspectRatio ?? undefined,
          height: fill ? undefined : height,
          ...containerStyle
        }}
      >
        <div className="flex h-full w-full items-center justify-center">
          {/* Fallback icon - you can replace this with your actual fallback SVG */}
          <Image
            src={(Fallback as unknown as {src: string})?.src}
            alt="fallback"
            width={iconStyles.width}
            height={iconStyles.height}
            style={{margin: 'auto'}}
            priority={priority}
          />
        </div>
      </div>
    ),
    [
      onClick,
      width,
      height,
      fill,
      containerStyle,
      fallbackWrapperClassName,
      iconStyles
    ]
  );

  // onError doesn't handle invalid src
  if (!src || !isValidSrc(src) || hasError) {
    return fallbackComponent;
  }

  return (
    <div
      className={cn('relative overflow-hidden', className)}
      style={{
        width: fill ? '100%' : width,
        aspectRatio: aspectRatio ?? undefined,
        height: fill ? undefined : height,
        ...containerStyle
      }}
      onClick={onClick}
    >
      {fill ? (
        <Image
          src={src}
          alt={alt || 'alt'}
          width={0}
          height={0}
          fill
          className={cn(
            'object-cover transition-opacity duration-300 ease-in-out',
            isLoading ? 'opacity-70' : 'opacity-100'
          )}
          priority={priority}
          loading={loading}
          placeholder={placeholder}
          blurDataURL={blurDataURL}
          sizes={sizes}
          quality={quality}
          unoptimized={unoptimized}
          onLoad={handleLoad}
          onError={handleError}
          style={
            style
              ? Object.fromEntries(
                  Object.entries(style).filter(
                    ([key]) => key !== 'height' && key !== 'width'
                  )
                )
              : undefined
          }
        />
      ) : (
        <Image
          src={src}
          alt={alt || 'alt'}
          width={typeof width == 'number' ? width : 400}
          height={typeof height == 'number' ? height : 400}
          className={cn(
            'transition-opacity duration-300 ease-in-out',
            isLoading ? 'opacity-70' : 'opacity-100'
          )}
          priority={priority}
          loading={loading}
          placeholder={placeholder}
          blurDataURL={blurDataURL}
          sizes={sizes}
          quality={quality}
          unoptimized={unoptimized}
          onLoad={handleLoad}
          onError={handleError}
          style={{
            height: height || 400,
            width: width || 400,
            maxHeight,
            maxWidth,
            ...style
          }}
        />
      )}
      {isLoading && (
        <div
          className="animate-shimmer absolute inset-0 animate-pulse rounded bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%]"
          style={{
            borderRadius:
              containerStyle?.borderRadius || style?.borderRadius || 0
          }}
        />
      )}
    </div>
  );
};

export {ImageWithFallback};
export type {ImageWithFallbackProps};
